import React, { Component } from "react";
import { ButtonGroup, ButtonToolbar, Col, Row } from "react-bootstrap";
import { FormattedMessage, injectIntl } from "react-intl";

import { faCheckCircle, faChevronLeft, faEye, faSave, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import ModalHeader from "react-bootstrap/ModalHeader";
import { NavLink } from "react-router-dom";
import { TEMPLATE_CREATOR } from "../../../common/constant";
import { isSectionLibraryEnabled, isSurveyValidationEnabled } from "../../../common/runtimeConfig";
import { Prompt } from "../../../common/prompt";
import Visibility from "../../../component/common/VisibleContainer";
import { DAADLogo } from "../../app/DAADLogo";
import HeaderContainer from "../../app/HeaderContainer";
import TemplateHeaderEdit from "../surveyEditor/TemplateHeaderEdit";
import TemplateHeaderView from "../surveyEditor/TemplateHeaderView";
import SurveyFooterContainer from "../surveyView/SurveyFooterContainer";
import SurveyViewContainer from "../surveyView/SurveyViewContainer";
import PagesPanel from "./PagesPanel";
import PrivateQuestionLibraryContainer from "./questionLibrary/PrivateQuestionLibraryContainer";
import SectionLibrary from "./sectionLibrary/SectionLibrary";
import QuestionLibraryContainer from "./questionLibrary/QuestionLibraryContainer";
import ToolboxPanel from "./toolbox/ToolboxPanel";
import { validateSurvey } from "../../../utils/SurveyValidationUtils";
import SurveyValidationDialog from "./SurveyValidationDialog";

/**
 * Survey editor component used to create surveys using predefined set of questions.
 * Survey editor contains one or many pages (tabs) with questions in each one of them.
 * All logic is handled in ducks/surveyEditor.js, following the Redux pattern.
 */
class SurveyEditor extends Component {
  constructor(props) {
    super(props);
    this.state = { validationResult: null, validationTemplateJson: null };
    this.bodyRef = React.createRef();
    this.handlePageChange = this.handlePageChange.bind(this);
  }

  componentDidMount() {
    const { clearAllMessages } = this.props;
    clearAllMessages();
    this.props.loadAllData();
    this.props.getQuestionLibraryFromOverview();
  }

  handlePageChange() {
    this.bodyRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }

  renderPreview() {
    const { editorPages, intl, surveyName } = this.props;
    const template = { pages: editorPages };
    const confirmLabel = intl.formatMessage({ id: "app.common.Close" });
    return (
      <Modal show className="modal-full" scrollable onHide={this.onHidePreview.bind(this)}>
        <Modal.Header className="bg-light" closeButton>
          <Modal.Title>
            <DAADLogo />
            {surveyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body ref={this.bodyRef}>
          <SurveyViewContainer
            onPageChange={this.handlePageChange}
            surveyTemplate={template}
            onConfirm={this.onHidePreview.bind(this)}
            confirmLabel={confirmLabel}
          />
        </Modal.Body>
        <Modal.Footer className="bg-dark text-light">
          <SurveyFooterContainer
            onConfirm={this.onHidePreview.bind(this)}
            confirmLabel={confirmLabel}
            surveyTemplate={template}
          />
        </Modal.Footer>
      </Modal>
    );
  }

  renderSyncModal() {
    const { identifier, version } = this.props;
    return (
      <Modal show enforceFocus autoFocus>
        <ModalHeader>
          <Modal.Title>
            <FormattedMessage id="template.activation.title" />
          </Modal.Title>
        </ModalHeader>
        <Modal.Body>
          <FormattedMessage id="template.question.sync.dialog.message" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.hideQuestionSyncDialog.bind(this)}>
            <FormattedMessage id="app.common.Close" />
          </Button>
          <Button onClick={() => this.sync(identifier, version)}>
            <FormattedMessage id="app.common.activate" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  onSaveSurvey() {
    const { saveSurvey } = this.props;
    saveSurvey(this.props.identifier, this.props.surveyName, this.props.surveyAdminLink);
  }

  onPreview() {
    const { previewSurvey } = this.props;
    previewSurvey();
  }

  onValidateSurvey() {
    const { editorPages, surveyTemplate } = this.props;
    this.setState({
      validationResult: validateSurvey(editorPages),
      validationTemplateJson: JSON.stringify({ ...surveyTemplate, pages: editorPages }, null, 2)
    });
  }

  onHidePreview() {
    const { hidePreviewSurvey } = this.props;
    hidePreviewSurvey();
  }

  showQuestionSyncDialog() {
    const { showQuestionSyncDialog } = this.props;
    showQuestionSyncDialog();
  }

  hideQuestionSyncDialog() {
    const { hideQuestionSyncDialog } = this.props;
    hideQuestionSyncDialog();
  }

  sync() {
    const { saveAndSyncSurveyQuestions, identifier, surveyName, surveyAdminLink } = this.props;
    saveAndSyncSurveyQuestions(identifier, surveyName, surveyAdminLink);
  }

  render() {
    const {
      editSurveyHeader,
      surveyName,
      showSurveyPreview,
      surveyNameDraft,
      surveyDescriptionDraft,
      propertyChange,
      changeEditState,
      onConfirm,
      doLogout,
      role,
      showHint,
      questionSync,
      syncDialogOpen,
      intl
    } = this.props;

    return (
      <>
        <Prompt message={intl.formatMessage({ id: "dialog.template.leave.text" })} />
        {showSurveyPreview && this.renderPreview()}
        {syncDialogOpen && this.renderSyncModal()}
        {isSurveyValidationEnabled() && this.state.validationResult && (
          <SurveyValidationDialog
            result={this.state.validationResult}
            templateJson={this.state.validationTemplateJson}
            isAdminUser={this.props.isAdminUser}
            onHide={() => this.setState({ validationResult: null, validationTemplateJson: null })}
          />
        )}
        <Row noGutters className="flex-fill">
          <Col xs={4} sm={4} md={3} lg={2} className="bg-dark text-light p-4">
            <p className="mb-3">
              <FormattedMessage id="question.library.toolbox.library" />
            </p>
            <ButtonToolbar className="mb-5">
              <ButtonGroup vertical className="gap-sm w-100">
                <QuestionLibraryContainer />
                <Visibility roles={[TEMPLATE_CREATOR]}>
                  <PrivateQuestionLibraryContainer />
                  {isSectionLibraryEnabled() && <SectionLibrary />}
                </Visibility>
              </ButtonGroup>
            </ButtonToolbar>
            <p className="mb-3">
              <FormattedMessage id="question.library.toolbox.custom" />
            </p>
            <ToolboxPanel />
          </Col>
          <Col xs={8} sm={8} md={9} lg={10}>
            <HeaderContainer page="editor" navIconsOnly={true} roles={role} doLogout={doLogout} showHint={showHint} />
            <Container fluid>
              <Row className="my-4">
                <Col>
                  <Button as={NavLink} variant="secondary" to="/userArea/templates">
                    <FontAwesomeIcon size="sm" icon={faChevronLeft} className="me-1" />
                    <FormattedMessage id="templateList.title" />
                  </Button>
                </Col>
                <Col className="text-end">
                  <ButtonGroup className="gap-sm">
                    {questionSync && (
                      <Button variant="secondary" onClick={this.showQuestionSyncDialog.bind(this)}>
                        <FontAwesomeIcon icon={faSync} className={"me-2"} />
                        <FormattedMessage id="templateList.tooltip.sync" />
                      </Button>
                    )}
                    {isSurveyValidationEnabled() && (
                      <Button variant="secondary" onClick={this.onValidateSurvey.bind(this)}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        <FormattedMessage id="survey.editor.validation.check" />
                      </Button>
                    )}
                    <Button variant="secondary" onClick={this.onPreview.bind(this)}>
                      <FontAwesomeIcon icon={faEye} className={"me-2"} />
                      <FormattedMessage id="survey.editor.preview" />
                    </Button>
                    <Button onClick={this.onSaveSurvey.bind(this)}>
                      <FontAwesomeIcon icon={faSave} className={"me-2"} />
                      <FormattedMessage id="survey.editor.save" />
                    </Button>
                  </ButtonGroup>
                </Col>
              </Row>
              {editSurveyHeader ? (
                <TemplateHeaderEdit
                  surveyNameDraft={surveyNameDraft}
                  surveyDescriptionDraft={surveyDescriptionDraft}
                  propertyChange={propertyChange}
                  onConfirm={onConfirm}
                  changeEditState={changeEditState}
                />
              ) : (
                <TemplateHeaderView surveyName={surveyName} changeEditState={changeEditState} />
              )}
              <PagesPanel {...this.props} />
            </Container>
          </Col>
        </Row>
      </>
    );
  }

  resetSurvey() {
    const { resetSurvey, resetSurveyEditor } = this.props;
    resetSurvey();
    resetSurveyEditor();
  }
}

SurveyEditor.propTypes = {
  identifier: PropTypes.string,
  surveyName: PropTypes.string,
  surveyTemplate: PropTypes.object,
  propertyChange: PropTypes.func.isRequired,
  saveSurvey: PropTypes.func,
  resetSurvey: PropTypes.func,
  resetSurveyEditor: PropTypes.func,
  surveyAdminLink: PropTypes.string,
  link: PropTypes.string,
  previewSurvey: PropTypes.func,
  hidePreviewSurvey: PropTypes.func,
  onConfirm: PropTypes.func,
  showSurveyPreview: PropTypes.bool,
  showQuestionSyncDialog: PropTypes.func,
  syncDialogOpen: PropTypes.bool,
  saveAndSyncSurveyQuestions: PropTypes.func,
  editorPages: PropTypes.array.isRequired,
  surveyNameDraft: PropTypes.string,
  surveyDescriptionDraft: PropTypes.string,
  editSurveyHeader: PropTypes.bool,
  smallHeaderExpanded: PropTypes.bool,
  toggleSmallHeaderAction: PropTypes.func,
  changeEditState: PropTypes.func,
  showDialog: PropTypes.func,
  pages: PropTypes.array,
  clearAllMessages: PropTypes.func,
  doLogout: PropTypes.func,
  role: PropTypes.array,
  isAdminUser: PropTypes.bool,
  loadAllData: PropTypes.func,
  getQuestionLibraryFromOverview: PropTypes.func
};

export default injectIntl(SurveyEditor);
